import { useCallback, useEffect, useState } from 'react';

import { IBackendCartEntries, IHistoricalOrder } from '@rbi-ctg/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { OrderStatus, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';
import { isCatering } from 'utils/service-mode';

import { isOfferType } from '../utils';

export const useReorder = (order?: IHistoricalOrder | null) => {
  const [cartVersionVerified, setCartVersionVerified] = useState(false);
  const [hasOffer, setHasOffer] = useState(false);
  const {
    reorder: { handleReorder, setPendingReorder },
    verifyCartVersion,
  } = useOrderContext();
  const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const { navigate, location, setStoreLocatorCallbackUrl } = useLocationContext();
  const enableReorder = useFlag(LaunchDarklyFlag.ENABLE_REORDER);

  // verify the cart version
  useEffect(() => {
    if (order) {
      const { cartVersion } = order.cart;
      verifyCartVersion(cartVersion, {
        message: false,
        onSuccess: () => {
          setCartVersionVerified(true);
        },
        onFailure: () => {
          setCartVersionVerified(false);
        },
      });
    }
  }, [order, verifyCartVersion]);

  /**
   * TODO: Add support for offers with reorder.
   * - right now just disabling reorder if there's an offer.
   */
  useEffect(() => {
    const hasOfferEntry =
      order && order.cart
        ? (order.cart.cartEntries || []).some((entry: IBackendCartEntries) =>
            isOfferType(entry.type)
          )
        : false;

    setHasOffer(hasOfferEntry);
  }, [order]);

  const handleReorderClick = useCallback(() => {
    if (!order) {
      return;
    }
    if (isStoreOpenAndAvailable) {
      return handleReorder(order);
    }
    const route = isCatering(order.cart.serviceMode)
      ? routes.serviceModeCatering
      : routes.storeLocator;
    setPendingReorder(order);
    setStoreLocatorCallbackUrl(location.pathname);
    navigate(route, { replace: true });
  }, [
    order,
    isStoreOpenAndAvailable,
    setPendingReorder,
    setStoreLocatorCallbackUrl,
    location.pathname,
    navigate,
    handleReorder,
  ]);

  const serviceMode = order?.cart.serviceMode ?? null;
  const isCateringWithWrongStatus =
    isCatering(serviceMode) && order?.status !== OrderStatus.CATERING_SUCCESSFUL;

  const disabled =
    !enableReorder ||
    !cartVersionVerified ||
    hasOffer ||
    isCateringWithWrongStatus ||
    isCatering(serviceMode) ||
    (!isStoreOpenAndAvailable && !noStoreSelected);

  return { handleReorderClick, disabled };
};
